import { Box, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import getErrorMessage from '../../assets/utils/getErrorMessage';
import { useToastNotificationContext } from '../../hooks/global/alert/useToastNotification';
import ErrorResponseObject from '../../interfaces/errorResponseObject';
import LimitViewReportProps from '../../interfaces/userRoleSettingsProps';
import customerAuthService from '../../services/customerAuthService';
import SwitchButtonComponent from '../whistleblower/twoFactorAuthentication/switchButtonComponent';

interface ReportsDownloadComponentProps extends LimitViewReportProps {
  isAllowedToDownloadAttachment: boolean;
  isAllowedToDownloadFinalReport: boolean;
  isError: boolean;
  mutate: () => void;
}

function ReportsDownloadComponent({
  userId,
  isAllowedToDownloadAttachment,
  isAllowedToDownloadFinalReport,
  isError,
  mutate,
}: ReportsDownloadComponentProps) {
  const { createToastNotification } = useToastNotificationContext();
  const intl = useIntl();

  const [switchOneState, setSwitchOneState] = useState(false);
  const [switchTwoState, setSwitchTwoState] = useState(false);

  useEffect(() => {
    setSwitchOneState(isAllowedToDownloadFinalReport);
    setSwitchTwoState(isAllowedToDownloadAttachment);
  }, [isAllowedToDownloadAttachment, isAllowedToDownloadFinalReport]);

  const updateSwitchOneStateViaApi = async (newState: boolean) => {
    setSwitchOneState(newState);

    try {
      await customerAuthService.updateAllowDownloadFinalReport(
        userId,
        localStorage.getItem('slug'),
        newState,
      );
      mutate();
      createToastNotification(
        intl.formatMessage({
          defaultMessage: 'User details successfully updated.',
        }),
        'success',
      );
      setSwitchOneState(newState);
    } catch (err) {
      const customError = getErrorMessage(err as ErrorResponseObject);
      createToastNotification(customError, 'error');
      setSwitchOneState(!newState);
    }
  };

  const updateSwitchTwoStateViaApi = async (newState: boolean) => {
    setSwitchTwoState(newState);
    try {
      await customerAuthService.updateAllowDownloadAttachments(
        userId,
        localStorage.getItem('slug'),
        newState,
      );
      mutate();
      createToastNotification(
        intl.formatMessage({
          defaultMessage: 'User details successfully updated.',
        }),
        'success',
      );
      setSwitchTwoState(newState);
    } catch (err) {
      const customError = getErrorMessage(err as ErrorResponseObject);
      createToastNotification(customError, 'error');
      setSwitchTwoState(!newState);
    }
  };

  const handleChangeSwitchOne = () => {
    const newState = !switchOneState;
    updateSwitchOneStateViaApi(newState);
  };

  const handleChangeSwitchTwo = () => {
    const newState = !switchTwoState;
    updateSwitchTwoStateViaApi(newState);
  };

  return (
    <>
      <hr
        style={{
          border: 'none',
          borderTop: '1px solid rgba(209, 209, 209, 1)',
          width: '752px',
          height: '1px',
          marginTop: '24px',
        }}
      />
      <Box sx={{ width: 'auto', height: '56px', mt: '24px', mb: '44px' }}>
        <Typography
          sx={{
            fontFamily: 'Schibsted Grotesk',
            fontSize: '16px',
            fontWeight: 500,
            textAlign: 'left',
            mb: '16px',
          }}
        >
          <FormattedMessage defaultMessage='Downloads' />
        </Typography>

        {/* Allow to download final report switch */}
        <Box
          sx={{
            mt: '16px',
            ml: '20px',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            alignContent: 'center',
          }}
        >
          <SwitchButtonComponent
            toggleSwitchState={switchOneState}
            onToggle={handleChangeSwitchOne}
          />
          <Typography
            sx={{
              fontFamily: 'Schibsted Grotesk',
              color: 'grey',
              fontSize: '14px',
              fontWeight: 400,
              ml: '12px',
            }}
          >
            <FormattedMessage defaultMessage='User is permitted to download final report' />
          </Typography>
        </Box>
        <Box
          sx={{
            mt: '20px',
            ml: '20px',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            alignContent: 'center',
          }}
        >
          <SwitchButtonComponent
            toggleSwitchState={switchTwoState}
            onToggle={handleChangeSwitchTwo}
          />
          <Typography
            sx={{
              fontFamily: 'Schibsted Grotesk',
              color: 'grey',
              fontSize: '14px',
              fontWeight: 400,
              ml: '12px',
            }}
          >
            <FormattedMessage defaultMessage='User is permitted to download attachments' />
          </Typography>
        </Box>
      </Box>

      {isError ? (
        <Typography
          sx={{
            fontFamily: 'Schibsted Grotesk',
            color: 'red',
            fontSize: '12px',
            fontWeight: 400,
            ml: '12px',
          }}
        >
          <FormattedMessage defaultMessage='Unable to load data.' />
        </Typography>
      ) : (
        ''
      )}
    </>
  );
}

export default ReportsDownloadComponent;
